/**
 * Tournage, « **La prise parfaite** » (⟦C53 · A-170 §2⟧) : le pattern côté écran.
 *
 * Matt, 01/10/2026 : *« je ne comprends pas pourquoi le rond et la grille des tiers ne sont pas
 * calés […] On ne peut pas inventer un autre jeu ? »* Le jeu est une jauge de jeu d'acteur : un
 * curseur va et vient, une zone verte marque le moment juste, REC quand il est dedans.
 *
 * ## Ce fichier ne contient presque rien, et c'est voulu
 *
 * La position du curseur est une **fonction déterministe du temps** que le serveur rejoue pour
 * noter : si le client en avait un jumeau, il montrerait un curseur et le serveur en noterait un
 * autre. Elle vit donc dans `shared/src/atelier/prise-parfaite.ts`, importée par les deux côtés,
 * ici on ne fait que traduire ce qui descend le fil (`depuisServeur`), générer le bac à sable avec
 * le MÊME générateur (`genererPriseParfaite`), noter pour l'écran avec le MÊME barème
 * (`noterPriseParfaite`), et dire les mots du jeu (`CONSIGNES`, `VERDICTS`).
 *
 * Aucun module sonore n'est importé ici : le son ne décide jamais (⟦A-22 · A-76⟧), et le test des
 * sons fait échouer tout fichier de barème qui en parlerait.
 */

import {
  PARDONS_MAX,
  PRISE_INTRO_PLANCHER,
  draw,
  exigenceAtelier,
  generateAtelierPattern,
  genererPrises,
  noterManche,
  reglagesPrises,
  type GameState,
  type IssueAppui,
  type NoteManche,
  type PriseParfaite,
  type VariantePrise,
} from "@maxime/shared";

import type { PatternTournageApi, TimelineTournage } from "../../api/atelier";
import { assists } from "../../game/moteur";

/** Le pattern d'écran : les cinq prises du serveur (ou du bac à sable), et les avantages de compétence. */
export interface PatternPriseParfaite {
  prises: readonly PriseParfaite[];
  /** *Multicam* : N prises ratées effacées de la moyenne (0 = aucun). */
  pardons: number;
  /** *Intro signature* : plancher de la première prise (0 = aucun). */
  premiereAssuree: number;
  /** Borne d'échelle (100, ou 85 en tolérance), jamais le plafond de compétence, voir `depuisServeur`. */
  scoreCap: number;
  /** Niveau d'exigence E. */
  k: number;
  /** ⟦A-83⟧ Mode posé (SPEC-UI §7.4) : curseur ralenti, aucune variante, aucune animation décorative. */
  pose: boolean;
}

/**
 * Ce que l'écran dit d'une variante : un badge (le nom, sur la scène) et une consigne d'une ligne (sur
 * la jauge). Le texte dit la règle, pas la solution : il ne dit ni quand l'acteur éternue ni où va
 * la zone. La règle complète est dans la leçon de Matt (`game/decouverte.ts`), lue une fois.
 */
export const CONSIGNES: Record<VariantePrise, { badge: string; consigne: string; court: string } | null> = {
  classique: null,
  // ⟦C57 · lot 8⟧ `court` : le mot de la jauge PENDANT la prise (4 mots au plus, jamais une phrase qui
  // oblige à lâcher la jauge des yeux). `consigne`, la phrase d'origine, reste : elle se lit derrière
  // « Les dessous » de l'écran de consigne et se dit au lecteur d'écran.
  mobile: { badge: "La zone glisse", consigne: "La zone glisse : suis-la du regard.", court: "Suis la zone." },
  vitesse: { badge: "Curseur capricieux", consigne: "Le curseur change de vitesse en route.", court: "Vitesse variable." },
  doree: { badge: "Prise en or", consigne: "Touche l'or au centre : bonus !", court: "Touche l'or !" },
  eternuement: { badge: "Il a le nez qui pique", consigne: "Il éternue ? Ne coupe pas, attends.", court: "Il éternue ? Attends." },
};

/** Les mots d'un verdict : ce que l'acteur et le réalisateur disent, et le son qui l'accompagne. */
export interface Verdict {
  /** La réplique, courte, dans la bulle. */
  texte: string;
  /** Une précision, en dessous. */
  detail: string;
  /** Une ligne du bilan, tenant dans une rangée de téléphone. */
  court: string;
  reussi: boolean;
}

export const VERDICTS: Record<IssueAppui, Verdict> = {
  doree: { texte: "Coupez, elle est en or !", detail: "Pile dans la zone dorée", court: "En or !", reussi: true },
  parfaite: { texte: "Coupez, elle est parfaite !", detail: "Pile au centre", court: "Parfaite !", reussi: true },
  bonne: { texte: "Coupez, elle est bonne !", detail: "Dans le vert", court: "Dans le vert", reussi: true },
  hors_zone: {
    texte: "On la refait",
    detail: "Le curseur n'était pas dans le vert",
    court: "Hors du vert",
    reussi: false,
  },
  eternuement: { texte: "Atchoum ! On la refait", detail: "Il ne fallait pas couper", court: "Atchoum !", reussi: false },
  sans_appui: { texte: "On la refait", detail: "Le curseur est allé au bout", court: "Trop tard", reussi: false },
  trop_tot: { texte: "On la refait", detail: "Trop tôt", court: "Trop tôt", reussi: false },
};

/**
 * Le pattern **servi par le serveur**, traduit tel quel : rien n'est recalculé ici.
 *
 * ⚠️ **Le `scoreCap` du fil n'est PAS celui du barème d'écran.** L'enveloppe du serveur porte
 * `exigenceAtelier(état, étape).score_cap`, qui **fusionne** la tolérance (85) et le **plafond de
 * compétence** ⟦A-9⟧ de la branche. Or le moteur applique déjà le second à la publication
 * (`scoresEffectifs`) et l'écran de l'Atelier le montre à part (« 92 · plafonné à 45 ») : le laisser
 * raboter le score du mini-jeu, ce serait l'appliquer deux fois et saboter le geste. On ne retient
 * donc que la borne qui ne dépend de rien, lue du moteur, exactement ce que fait la génération
 * en bac à sable, et ce que fait `validate.ts` (il ne connaît que 100 / 85).
 */
export function depuisServeur(api: PatternTournageApi, state: GameState): PatternPriseParfaite {
  return {
    prises: api.params.prises,
    pardons: api.params.pardons,
    premiereAssuree: api.params.premiereAssuree,
    scoreCap: exigenceAtelier(state).score_cap,
    k: api.tier,
    // ⟦A-83⟧ Le serveur a généré la variante posée si on la lui a demandée (§7.4).
    pose: api.reducedMotion,
  };
}

export interface OptionsPriseParfaite {
  state: GameState;
  seed: string;
  /** ⟦A-83⟧ `prefers-reduced-motion` : la variante posée (§7.4), jumelle du serveur. */
  pose?: boolean;
}

/**
 * Le pattern du **bac à sable** : le MÊME générateur que le serveur (`genererPrises`, `shared/`), un
 * autre tireur (le `draw` du moteur, reproductible dans le navigateur). Le bac à sable joue
 * toujours **le jeu de base** (aucune variante, aucune montée en cours de palier) : celles-là se
 * tirent côté serveur (⟦C47⟧), c'est le contrat, le bac à sable n'est pas un deuxième avis.
 */
export function genererPriseParfaite({ state, seed, pose = false }: OptionsPriseParfaite): PatternPriseParfaite {
  const exigence = exigenceAtelier(state);
  const a = assists(state, "bonne_prise");
  const reglages = reglagesPrises(exigence.k, 0, {
    tolerance: state.flags.accessibility_tolerance === true,
    zoneMult: 1 + (a.fenetres_pct ?? 0) / 100,
    eclairage: (a.phase_lumieres ?? 0) >= 1,
    pose,
  });
  const graine = generateAtelierPattern(state, "tournage", seed).seed_component;
  let i = 0;
  const tireur = { next: () => draw(graine, 0, "atelier_pattern", i++) };
  return {
    prises: genererPrises(tireur, { ...reglages, probaVariante: 0, variantes: [] }),
    pardons: Math.min(PARDONS_MAX, Math.max(0, Math.trunc(a.retakes_gratuites ?? 0))),
    premiereAssuree: (a.premiere_fenetre_reussie ?? 0) >= 1 ? PRISE_INTRO_PLANCHER : 0,
    scoreCap: exigence.score_cap,
    k: exigence.k,
    pose,
  };
}

/** Les appuis d'une manche, rangés par prise : `appuis[i]` est l'instant de la prise `i`, ou `null`. */
export function appuisParPrise(
  pattern: PatternPriseParfaite,
  timeline: TimelineTournage,
): (number | null)[] {
  const parPrise: (number | null)[] = pattern.prises.map(() => null);
  for (const a of timeline.appuis) if (a.prise >= 0 && a.prise < parPrise.length) parPrise[a.prise] = a.atMs;
  return parPrise;
}

/**
 * **La note d'une manche, refaite depuis la timeline** : exactement le barème du serveur
 * (`scorerTournage`, qui appelle le même `noterManche`), plafonné à la borne d'échelle du
 * pattern. C'est volontairement la timeline qu'on note, pas une accumulation prise par prise : le
 * serveur, lui, n'aura que la timeline.
 */
export function noterPriseParfaite(
  pattern: PatternPriseParfaite,
  timeline: TimelineTournage,
): NoteManche {
  const note = noterManche(pattern.prises, appuisParPrise(pattern, timeline), {
    pardons: pattern.pardons,
    premiereAssuree: pattern.premiereAssuree,
  });
  return { ...note, score: Math.max(0, Math.min(pattern.scoreCap, note.score)) };
}
